import React, { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import customInstance, { AXIOS_INSTANCE } from "../api/axios-instance";
import { mutate } from "swr";
import { UserDto } from "../api/model";
import useCookie from "../hooks/useCookie";
import {getApiUser, getGetApiUserKey, useGetApiUser} from "../api/endpoints";
import Loading from "../components/Loading";
import { AxiosRequestConfig } from "axios";
import jwtDecode, { JwtPayload } from "jwt-decode";
import useApi from "../utils/useApi";

interface AuthContextType {
  user?: UserDto;
  access_token: string | null;
  setAccessToken: (token: string | null) => void;
  // isAuthenticated: boolean;
  // isAuthenticadedFn: () => boolean;
  isAdmin: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>(null!);

const useAuth = () => useContext(AuthContext);

export const useAccessToken = () => {
  return useCookie({
    key: "access_token",
    defaultValue: "",
  });
};

export const checkToken = (token: string) => {
  console.log("checkToken",token);
  if (token === "") return false;
  try {
    var decoded1: JwtPayload = jwtDecode(token!, { header: false });
    var check =!!token && Date.now() / 1000 < decoded1?.exp!;
    console.log("check", check);
    return check;
  } catch (error) {
    console.log("check fail", error);
    
    return false;
  }
};

export const useRefreshToken = () => {
  return useCookie({
    key: "refresh_token",
    defaultValue: "",
  });
};

// Auth Provider
function AuthProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isLoginCallback = location.pathname === "/login-callback";
  console.log("authProvider for ", location.pathname, isLoginCallback);
  const navigate = useNavigate();
  const auth =   useAuth();
  const [accessToken, updateAccessToken] = useAccessToken();
  const [value, setValue] = useState<AuthContextType>({
    access_token: null,
    setAccessToken: (token: string | null) => {
      updateAccessToken(token, 30);
      // mutate(getGetApiUserKey());
    },
    // isAuthenticated: false,
    // isAuthenticadedFn: checkToken
    isAdmin: false,
    isLoading: true,
  });
  
  

  // const { data: user } = useGetApiUser({
  //   swr: {
  //     revalidateIfStale: false,
  //     revalidateOnFocus: false,
  //     revalidateOnReconnect: false,
  //   },
  // });


  useEffect(() => {
    if(isLoginCallback) {
      console.log("useEffect on AuthProvider for access token skip for login callback");
      return
    } 
    console.log("useEffect on AuthProvider for access token", accessToken);
    if(!checkToken(accessToken!)){
    
      console.error("Token is invalid or expired.");
      setValue((prev) => ({
        ...prev,
        access_token: null,
        // isAuthenticated: false,
        isAdmin: false,
        user: undefined,
      }));
      updateAccessToken(null, 0);

      navigate("/", {replace: true});
      return
    }else {
      console.log("access token verified");
      const interceptorId = AXIOS_INSTANCE.interceptors.request.use(
          (config: AxiosRequestConfig<any>) => {
            // if (accessToken && !checkToken(accessToken!)) {
            //   console.error("Token is invalid or expired.", accessToken, config);
            //   // debugger
            //   // if (config.url !== '/' && )
            //   //   navigate("/", { replace: true });
            // }
            if (accessToken) {
              console.log("setting bearer token", accessToken);
              config = {
                ...config,
                headers: {
                  ...config.headers,
                  Authorization: `Bearer ${accessToken}`,
                },
              } as AxiosRequestConfig<any>;
            }
            return config;
          },
          (error) => Promise.reject(error)
      );
      const responseInterceptorId = AXIOS_INSTANCE.interceptors.response.use(
          (response) => response,
          (error) => {
            console.error("Axios Error: ", error);

            if (error.code === "ERR_NETWORK") {
              navigate("/", {replace: true});
            }

            if (error.response) {
              if ([401, 403, 498, 302].includes(error.response.status)) {
                console.error("Token is invalid or expired.");
                setValue((prev) => ({
                  ...prev,
                  access_token: null,
                  // isAuthenticated: false,
                  isAdmin: false,
                  user: undefined,
                }));
                updateAccessToken(null, 0);

                navigate("/", {replace: true});
              }
            }

            return Promise.reject(error);
          }
          // }
          // }
      );

      // mutate(getGetApiUserKey());

      return () => {
        AXIOS_INSTANCE.interceptors.request.eject(interceptorId);
        AXIOS_INSTANCE.interceptors.response.eject(responseInterceptorId);
      };
    }
  }, [accessToken]);
  const {
    data: user,
    setData: setUser,
    loading: isLoadingUser,
    setLoading: setIsLoadingUser,
    error: errorUser,
    setError: setErrorUser,
    isError: isErrorUser,
  } = useApi<UserDto>(() => getApiUser(), [accessToken], !isLoginCallback);
  // Effect to update AuthContext value only when both accessToken and user are valid.
  useEffect(() => {
    if(isLoginCallback) {
      console.log("useEffect on AuthProvider 2 for access token skip for login callback");
      
      return
    }
    if (accessToken && user) {
      setValue((prevValue) => ({
        ...prevValue,
        user: user,
        access_token: accessToken,
        isAdmin: user.roles?.some((role) => role.toLowerCase() === "admin") || false,
        isLoading: isLoadingUser
      }));
    } else if (!accessToken) {
      setValue((prevValue) => ({
        ...prevValue,
        user: undefined,
        access_token: null,
        isAdmin: false,
        isLoading: false,
      }));
    }
  }, [user, accessToken]);
  // if(accessToken == null || accessToken == '' || isErrorUser){
  //  
  //   console.log("No access token provided");
  //  
  // } else{
  //   console.log("Access token provided ",accessToken, user, isLoadingUser, errorUser);
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  // }
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const auth = useAuth();
  const [accessToken, updateAccessToken] = useAccessToken();
  const [value, setValue] = useState<AuthContextType>({
    access_token: null,
    setAccessToken: (token: string | null) => {
      updateAccessToken(token, 30);
      mutate(getGetApiUserKey());
    },
    // isAuthenticated: false,
    isAdmin: false,
    isLoading: true,
  });
  const location = useLocation();

  if (
    !value.isLoading &&
    // !value.isAuthenticated ||
    (!value.access_token || !value.user || checkToken(value.access_token))
  ) {
    setValue((prevValue) => ({
      ...prevValue,
      access_token: null,
      // isAuthenticated: false,
      isAdmin: false,
      isLoading: true,
    }));
    
    console.log("Require Auth go back to home",value )
    
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // if (value.isLoading) {
  //   return <Loading />;
  // }

  console.log("Require Auth passed")
  
  return children;
}

export { useAuth, RequireAuth, AuthProvider };

  // On /login-callback, avoid trying to access user info until authentication is complete.
  // This is already handled by the `useApi` hook's enabled flag, but ensure no user access until ready.